import React, { forwardRef } from 'react';
import { DataGridPro, type DataGridProProps } from '@mui/x-data-grid-pro';
import { GridPagination } from '@mui/x-data-grid';

export type DensityMode = 'compact' | 'standard';

interface AnimatedDataGridProProps extends Omit<DataGridProProps, 'sx'> {
  density: DensityMode;
  animateRows?: boolean;
  sx?: any; // We'll merge our animation styles with user's sx
  autoHeight?: boolean; // Make autoHeight optional but default to true
  expandRowOnClick?: boolean; // Make entire row clickable for expand/collapse
  pagination?: boolean; // Enable pagination instead of scrollbars
  pageSize?: number; // Default page size for pagination
  compactRowHeight?: number;
  standardRowHeight?: number;
}

const paginationLayerSx = {
  '& .MuiDataGrid-main': {
    position: 'relative',
    zIndex: 1,
  },
  '& .MuiDataGrid-footerContainer': {
    position: 'relative',
    zIndex: 3,
    backgroundColor: '#FFFFFF',
  },
};

const gridPaginationMenuZIndex = 2600;

const mergeSx = (existingSx: any, nextSx: any) => {
  if (!existingSx) {
    return nextSx;
  }

  if (Array.isArray(existingSx)) {
    return [nextSx, ...existingSx];
  }

  return [nextSx, existingSx];
};

const mergeGridSlotProps = (slotProps: any) => {
  const resolvedSlotProps = slotProps ?? {};
  const existingBaseSelect = resolvedSlotProps.baseSelect ?? {};
  const existingMenuProps = (existingBaseSelect as any).MenuProps ?? {};
  const existingMenuSlotProps = existingMenuProps.slotProps ?? {};
  const existingPagination = resolvedSlotProps.pagination ?? {};
  const existingPaginationSelectProps = existingPagination.SelectProps ?? {};
  const existingPaginationMenuProps =
    existingPaginationSelectProps.MenuProps ?? {};
  const existingPaginationMenuSlotProps =
    existingPaginationMenuProps.slotProps ?? {};
  const existingPaginationSlotProps = existingPagination.slotProps ?? {};
  const existingPaginationSelectSlotProps =
    existingPaginationSlotProps.select ?? {};
  const existingPaginationSelectSlotMenuProps =
    existingPaginationSelectSlotProps.MenuProps ?? {};
  const existingPaginationSelectSlotMenuSlotProps =
    existingPaginationSelectSlotMenuProps.slotProps ?? {};

  return {
    ...resolvedSlotProps,
    baseSelect: {
      ...existingBaseSelect,
      MenuProps: {
        ...existingMenuProps,
        disablePortal: false,
        anchorOrigin: {
          vertical: 'top',
          horizontal: 'left',
        },
        transformOrigin: {
          vertical: 'bottom',
          horizontal: 'left',
        },
        sx: mergeSx(existingMenuProps.sx, {
          zIndex: gridPaginationMenuZIndex,
        }),
        PaperProps: {
          ...(existingMenuProps.PaperProps ?? {}),
          sx: mergeSx(existingMenuProps.PaperProps?.sx, {
            zIndex: gridPaginationMenuZIndex + 1,
          }),
        },
        slotProps: {
          ...existingMenuSlotProps,
          root: {
            ...(existingMenuSlotProps.root ?? {}),
            sx: mergeSx(existingMenuSlotProps.root?.sx, {
              zIndex: gridPaginationMenuZIndex,
            }),
          },
          paper: {
            ...(existingMenuSlotProps.paper ?? {}),
            sx: mergeSx(existingMenuSlotProps.paper?.sx, {
              zIndex: gridPaginationMenuZIndex + 1,
            }),
          },
        },
      },
    },
    pagination: {
      ...existingPagination,
      SelectProps: {
        ...existingPaginationSelectProps,
        MenuProps: {
          ...existingPaginationMenuProps,
          variant: 'menu',
          disablePortal: false,
          anchorOrigin: {
            vertical: 'top',
            horizontal: 'left',
          },
          transformOrigin: {
            vertical: 'bottom',
            horizontal: 'left',
          },
          sx: mergeSx(existingPaginationMenuProps.sx, {
            zIndex: gridPaginationMenuZIndex,
          }),
          PaperProps: {
            ...(existingPaginationMenuProps.PaperProps ?? {}),
            sx: mergeSx(existingPaginationMenuProps.PaperProps?.sx, {
              zIndex: gridPaginationMenuZIndex + 1,
            }),
          },
          slotProps: {
            ...existingPaginationMenuSlotProps,
            root: {
              ...(existingPaginationMenuSlotProps.root ?? {}),
              sx: mergeSx(existingPaginationMenuSlotProps.root?.sx, {
                zIndex: gridPaginationMenuZIndex,
              }),
            },
            paper: {
              ...(existingPaginationMenuSlotProps.paper ?? {}),
              sx: mergeSx(existingPaginationMenuSlotProps.paper?.sx, {
                zIndex: gridPaginationMenuZIndex + 1,
              }),
            },
          },
        },
      },
      slotProps: {
        ...existingPaginationSlotProps,
        select: {
          ...existingPaginationSelectSlotProps,
          MenuProps: {
            ...existingPaginationSelectSlotMenuProps,
            variant: 'menu',
            disablePortal: false,
            anchorOrigin: {
              vertical: 'top',
              horizontal: 'left',
            },
            transformOrigin: {
              vertical: 'bottom',
              horizontal: 'left',
            },
            sx: mergeSx(existingPaginationSelectSlotMenuProps.sx, {
              zIndex: gridPaginationMenuZIndex,
            }),
            PaperProps: {
              ...(existingPaginationSelectSlotMenuProps.PaperProps ?? {}),
              sx: mergeSx(
                existingPaginationSelectSlotMenuProps.PaperProps?.sx,
                {
                  zIndex: gridPaginationMenuZIndex + 1,
                },
              ),
            },
            slotProps: {
              ...existingPaginationSelectSlotMenuSlotProps,
              root: {
                ...(existingPaginationSelectSlotMenuSlotProps.root ?? {}),
                sx: mergeSx(
                  existingPaginationSelectSlotMenuSlotProps.root?.sx,
                  {
                    zIndex: gridPaginationMenuZIndex,
                  },
                ),
              },
              paper: {
                ...(existingPaginationSelectSlotMenuSlotProps.paper ?? {}),
                sx: mergeSx(
                  existingPaginationSelectSlotMenuSlotProps.paper?.sx,
                  {
                    zIndex: gridPaginationMenuZIndex + 1,
                  },
                ),
              },
            },
          },
        },
      },
    },
  };
};

const getMergedPaginationMenuProps = (menuProps: any) => {
  const existingMenuSlotProps = menuProps?.slotProps ?? {};

  return {
    ...menuProps,
    variant: 'menu',
    disablePortal: false,
    anchorOrigin: {
      vertical: 'top',
      horizontal: 'left',
    },
    transformOrigin: {
      vertical: 'bottom',
      horizontal: 'left',
    },
    sx: mergeSx(menuProps?.sx, {
      zIndex: gridPaginationMenuZIndex,
    }),
    PaperProps: {
      ...(menuProps?.PaperProps ?? {}),
      sx: mergeSx(menuProps?.PaperProps?.sx, {
        zIndex: gridPaginationMenuZIndex + 1,
      }),
    },
    slotProps: {
      ...existingMenuSlotProps,
      root: {
        ...(existingMenuSlotProps.root ?? {}),
        sx: mergeSx(existingMenuSlotProps.root?.sx, {
          zIndex: gridPaginationMenuZIndex,
        }),
      },
      paper: {
        ...(existingMenuSlotProps.paper ?? {}),
        sx: mergeSx(existingMenuSlotProps.paper?.sx, {
          zIndex: gridPaginationMenuZIndex + 1,
        }),
      },
    },
  };
};

const ModalGridPagination = forwardRef<any, any>(function ModalGridPagination(
  props,
  ref,
) {
  const existingSelectProps = props?.SelectProps ?? {};
  const existingSelectSlotProps = props?.slotProps ?? {};
  const existingSelectSlot = existingSelectSlotProps.select ?? {};

  return (
    <GridPagination
      ref={ref}
      {...props}
      SelectProps={{
        ...existingSelectProps,
        MenuProps: getMergedPaginationMenuProps(existingSelectProps.MenuProps),
      }}
      slotProps={{
        ...existingSelectSlotProps,
        select: {
          ...existingSelectSlot,
          MenuProps: getMergedPaginationMenuProps(existingSelectSlot.MenuProps),
        },
      }}
    />
  );
});

// Create dynamic styles based on row height for animated row transitions
const getDynamicRowStyles = (rowHeight: number, expandRowOnClick: boolean = false) => {
  return {
    // Set CSS variables that override MUI's internal variables
    '--DataGrid-rowHeight': `${rowHeight}px !important`,
    '--density-row-height': `${rowHeight}px !important`,
    '--height': `${rowHeight}px !important`,
    
    // Focus on smooth container transitions + individual row animations
    '&.MuiDataGrid-root': {
      // Main DataGrid container smooth transitions
      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important',
      
      '& .MuiDataGrid-main': {
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important',
      },
      
      '& .MuiDataGrid-virtualScroller': {
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important',
      },
      
      '& .MuiDataGrid-virtualScrollerContent': {
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important',
      },
      
      '& .MuiDataGrid-virtualScrollerRenderZone': {
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important',
      },

      '& .MuiDataGrid-scrollbar--vertical': {
        display: 'none !important',
      },
      
      // Individual row animations
      '& .MuiDataGrid-row': {
        height: `${rowHeight}px !important`,
        minHeight: `${rowHeight}px !important`,
        maxHeight: `${rowHeight}px !important`,
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important',
        willChange: 'height',
      },
      
      '& .MuiDataGrid-cell': {
        height: `${rowHeight}px !important`,
        minHeight: `${rowHeight}px !important`,
        maxHeight: `${rowHeight}px !important`,
        lineHeight: `${rowHeight - 1}px !important`,
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important',
        display: 'flex !important',
        alignItems: 'center !important',
        willChange: 'height',
      },
      
      // Row fade-in animation
      '& .MuiDataGrid-row.row--animate': {
        animation: 'rowFadeIn 240ms ease-out',
      },
      
      // Enhanced expand/collapse functionality
      '& .MuiDataGrid-treeDataGroupingCell': {
        padding: '0 8px !important',
        
        '& .MuiDataGrid-treeDataGroupingCellToggle': {
          width: '32px !important',
          height: '32px !important',
          minWidth: '32px !important',
          minHeight: '32px !important',
          borderRadius: '4px !important',
          display: 'flex !important',
          alignItems: 'center !important',
          justifyContent: 'center !important',
          cursor: 'pointer !important',
          transition: 'all 0.2s ease-in-out !important',
          
          '&:hover': {
            backgroundColor: 'rgba(0, 165, 255, 0.1) !important',
            transform: 'scale(1.1) !important',
          },
          
          '& .MuiSvgIcon-root': {
            fontSize: '18px !important',
            color: '#00A5FF !important',
            transition: 'all 0.2s ease-in-out !important',
          },
        },
      },
      
      // Enhanced group row styling
      '& .MuiDataGrid-row--group': {
        backgroundColor: 'rgba(245, 247, 252, 0.8) !important',
        fontWeight: '600 !important',
        
        '&:hover': {
          backgroundColor: 'rgba(0, 165, 255, 0.06) !important',
        },
      },
      
      // Expanded row styling
      '& .MuiDataGrid-row--group.MuiDataGrid-row--expanded': {
        '& .MuiDataGrid-treeDataGroupingCellToggle': {
          backgroundColor: 'rgba(0, 165, 255, 0.15) !important',
          
          '& .MuiSvgIcon-root': {
            transform: 'rotate(90deg) !important',
          },
        },
      },
      
      // Visual indicator for expandable rows
      '& .MuiDataGrid-row[aria-expanded]': {
        position: 'relative',
        
        '&::before': {
          content: '""',
          position: 'absolute',
          left: '0',
          top: '0',
          bottom: '0',
          width: '3px',
          backgroundColor: '#00A5FF',
          opacity: '0.3',
          transition: 'opacity 0.2s ease-in-out',
        },
        
        '&:hover::before': {
          opacity: '0.6',
        },
        
        '&[aria-expanded="true"]::before': {
          opacity: '1',
        },
        
        // Make entire row clickable for expand if prop is enabled
        ...(expandRowOnClick && {
          cursor: 'pointer !important',
          
          '& .MuiDataGrid-cell': {
            cursor: 'pointer !important',
          },
          
          '&:hover': {
            backgroundColor: 'rgba(0, 165, 255, 0.08) !important',
          },
        }),
      },
      
      '@keyframes rowFadeIn': {
        from: { opacity: 0 },
        to: { opacity: 1 },
      },
    },
  };
};

const AnimatedDataGridPro = forwardRef<HTMLDivElement, AnimatedDataGridProProps>(
({
  density,
  animateRows = false,
  autoHeight = true,
  expandRowOnClick = false,
  pagination = false,
  pageSize = 10,
  compactRowHeight = 32,
  standardRowHeight = 40,
  sx,
  slots,
  slotProps,
  getRowClassName,
  rowHeight,
  ...otherProps
}, ref) => {
  // Calculate dimensions
  const resolvedRowHeight =
    typeof rowHeight === 'number'
      ? rowHeight
      : density === 'compact'
        ? compactRowHeight
        : standardRowHeight;
    
  // Create enhanced getRowClassName that includes animation classes
  const enhancedGetRowClassName = (params: any) => {
    const classes = [];
      
    // Add animation class if enabled
    if (animateRows) {
      classes.push('row--animate');
    }
      
    // Call original getRowClassName if provided
    if (getRowClassName) {
      const originalClasses = getRowClassName(params);
      if (originalClasses) {
        classes.push(originalClasses);
      }
    }
      
    return classes.join(' ');
  };

  // Get base animation styles
  const animationStyles = getDynamicRowStyles(resolvedRowHeight, expandRowOnClick);
  const mergedSlotProps = mergeGridSlotProps(slotProps);
  const mergedSlots = pagination
    ? {
        ...(slots ?? {}),
        pagination: ModalGridPagination,
      }
    : slots;

  // Merge with user's sx
  const baseMergedSx = Array.isArray(sx)
    ? [...sx, animationStyles]
    : sx
      ? [sx, animationStyles]
      : animationStyles;
  const mergedSx = pagination
    ? (Array.isArray(baseMergedSx)
        ? [...baseMergedSx, paginationLayerSx]
        : [baseMergedSx, paginationLayerSx])
    : baseMergedSx;

  // Configure pagination props
  const paginationProps = pagination ? {
    pagination: true,
    pageSizeOptions: [5, 10, 25, 50, 100],
    initialState: {
      pagination: { paginationModel: { pageSize } },
    },
    hideFooter: false, // Always show footer when pagination is enabled
  } : {
    hideFooter: otherProps.hideFooter !== false, // Only hide footer when explicitly requested
  };

  return (
    <DataGridPro
      ref={ref}
      density={density}
      rowHeight={resolvedRowHeight}
      autoHeight={autoHeight}
      getRowClassName={enhancedGetRowClassName}
      sx={mergedSx}
      slots={mergedSlots}
      slotProps={mergedSlotProps}
      {...paginationProps}
      {...otherProps}
    />
  );
  }
);

AnimatedDataGridPro.displayName = 'AnimatedDataGridPro';

export default AnimatedDataGridPro;
